import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { agentMeta, VERDICT } from "@/sim/lib";
import { cn } from "@/lib/utils";

export function AgentFeed({ agents = [], assignedCount = 9, status }) {
  const endRef = useRef(null);
  const doneIds = new Set(agents.map((a) => a.agent_id));
  const pending = Math.max(0, assignedCount - agents.length);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [agents.length]);

  return (
    <div className="sim-terminal rounded-xl flex flex-col h-full min-h-[300px]" data-testid="sim-feed">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1c1c1f] relative z-10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#10B981]/60" />
        <span className="sim-mono text-[10px] text-[#C2C5CE] ml-2">agent_deliberation.log</span>
        {status !== "COMPLETED" && status !== "FAILED" && (
          <span className="sim-mono text-[10px] text-[#00F0FF] ml-auto animate-pulse">● LIVE</span>
        )}
      </div>
      <div className="flex-1 overflow-auto sim-scroll p-3 relative z-10 max-h-[460px]">
        <AnimatePresence initial={false}>
          {agents.map((a) => {
            const meta = agentMeta(a.agent_id);
            const v = VERDICT[a.verdict] || VERDICT.HESITANT;
            return (
              <motion.div
                key={a.agent_id}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                className="sim-mono text-[11px] leading-relaxed mb-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[#9AA0AC]">&gt;</span>
                  <span style={{ color: meta.accent }}>[{a.agent_id}]</span>
                  <span className={cn("font-bold", v.text)}>{v.label.toUpperCase()}</span>
                  <span className="text-[#C2C5CE]">· {a.score}/100</span>
                </div>
                <p className="text-[#D4D4D8] pl-4 mt-0.5">{a.summary}</p>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {pending > 0 && status !== "FAILED" && (
          <div className="sim-mono text-[11px] text-[#9AA0AC] flex items-center gap-2">
            <span className="text-[#9AA0AC]">&gt;</span>
            <span className="sim-cursor">evaluating {pending} more agent{pending > 1 ? "s" : ""}</span>
          </div>
        )}
        {status === "FAILED" && (
          <div className="sim-mono text-[11px] text-[#EF4444]">&gt; simulation aborted.</div>
        )}
        <div ref={endRef} />
      </div>
    </div>
  );
}
